// The four theme palettes as raw hex/rgba (+ a `font` stack), frozen. This is the single
// source for both the `--c-*` CSS-var emission (GlobalStyles) and the JS computation sites that
// need a raw string (SVG attributes, hex-alpha, PDF), which read `themeColors(theme)`.
export const THEMES = Object.freeze({
  dark: {
    font: '"IBM Plex Mono",monospace',
    bg: "#060610",
    panel: "#0c0c1e",
    past: "#040408",
    future: "#1F0D26",
    purple: "#9966dd",
    purpleDim: "#5533aa",
    purpleL: "#cc99ff",
    yellow: "#F7CE46",
    gray: "#667788",
    grayL: "#aabbcc",
    white: "#ffffff",
    orange: "#ff9933",
    green: "#44dd88",
    red: "#ff4455",
    // Destructive-action red, deliberately NOT `red` above: three of the four
    // palettes neutralise `red` to grey/white to stay monochrome, which left the
    // one irreversible control in the app reading as ordinary text. This token
    // stays red in every theme — tuned per background, never neutralised.
    danger: "#ff4455",
    border: "#1e2a44",
    // Panel and rail edges: a step brighter than `border`, so panels stand off the canvas.
    edge: "#2e3d5c",
    today: "#F7CE46",
    pieBg: "#0a0a20",
    inputBg: "#0e0e22",
    sidebarBg: "#090914",
    tooltipBg: "#111428",
    rowInactive: "#171725",
    rowActive: "#1e1e2f",
    // The edge that marks the SELECTED card. Its own token because a theme's `purple` is
    // not always purple — Dawn's is a blue, dev's a gold — and `purple` is spelled in too
    // many other places to redefine safely.
    rowSelectedEdge: "#9966dd",
    userMsg: "#121330",
    asstMsg: "#090f09",
    asstBorder: "#1a3020",
    dropHover: "#151530",
    pastFill: "#0d1520",
    pastHover: "#1e2840",
    btnGreen: "#113322",
    btnRed: "#331122",
    debugBg: "#050510",
    btnBg: "#1a1a2e",
    sectionBg: "#0a0a1e",
    srcBg: "#0a0a1a",
    modalOverlay: "rgba(0,0,0,.65)",
    loadingOverlay: "rgba(4,4,12,0.85)",
    shadow: "rgba(0,0,0,.7)",
    shadowBox: "0 6px 28px rgba(0,0,0,.8)",
    toolbarBg: "#080818",
    debugPanelBg: "#060612",
    toolbarBtn: "#111128",
    disabledBorder: "#1a1a1a",
    redTeam: "#ff4455",
    blueTeam: "#4488ff",
    btnText: "#ffffff",
    chatInputBg: "#20203a",
    action: "#F7CE46",
    actGreen: "#44dd88",
    actRed: "#ff4455",
    actNeutral: "#aabbcc",
    accent: "#9966dd",
    // Semantic gold for settled/winning/help places, distinct from `action`. Dawn draws it green.
    gold: "#F7CE46",
  },
  // "dusk" (Dawn) — light copy of the "dev"/Dark theme. It shares Dark's component treatment
  // via the `theme==="dev"||theme==="dusk"` gates. Its blue action color is luminance-matched
  // to Dark's gold, so dark btnText still reads on action buttons. The trigger green is a darker
  // emerald because Dark's mint washes out on white.
  dusk: {
    font: '"IBM Plex Mono",monospace',
    bg: "#f7f6fa",
    panel: "#ffffff",
    past: "#f1eff5",
    future: "#f7f6fa",
    purple: "#D783FF",
    purpleDim: "#a29db3",
    purpleL: "#D783FF",
    yellow: "#47435a",
    gray: "#6a6679",
    grayL: "#3b3848",
    white: "#18161e",
    orange: "#9a5b00",
    green: "#D783FF",
    red: "#b5442e",
    danger: "#c62828",
    border: "#e0dce8",
    // Panel and rail edges: a step brighter than `border`, so panels stand off the canvas.
    edge: "#c6bfd3",
    today: "#18161e",
    pieBg: "#efedf4",
    inputBg: "#ffffff",
    sidebarBg: "#f1eff5",
    tooltipBg: "#ffffff",
    // Dawn selects by getting brighter, like the dark themes, but white is the ceiling, so the
    // contrast comes from the resting card sitting just below the selected one rather than the
    // selected one climbing.
    rowInactive: "#f1eff5",
    rowActive: "#ffffff",
    rowSelectedEdge: "#D783FF",
    userMsg: "#efedf4",
    asstMsg: "#f7f6fa",
    asstBorder: "#e0dce8",
    dropHover: "#ebe8f1",
    pastFill: "#efedf4",
    pastHover: "#e5e1ec",
    btnGreen: "#efedf4",
    btnRed: "#efedf4",
    debugBg: "#f7f6fa",
    btnBg: "#efedf4",
    sectionBg: "#ffffff",
    srcBg: "#f7f6fa",
    modalOverlay: "rgba(24,22,30,.38)",
    loadingOverlay: "rgba(247,246,250,.86)",
    shadow: "rgba(24,22,30,.14)",
    shadowBox: "0 16px 48px rgba(24,22,30,.14)",
    toolbarBg: "#f1eff5",
    debugPanelBg: "#f7f6fa",
    toolbarBtn: "#efedf4",
    disabledBorder: "#e0dce8",
    redTeam: "#c2412d",
    blueTeam: "#1f8a5f",
    btnText: "#0a0a0a",
    chatInputBg: "#ffffff",
    action: "#D783FF",
    actGreen: "#D783FF",
    actRed: "#D783FF",
    actNeutral: "#D783FF",
    accent: "#D783FF",
    gold: "#1f8a5f",
  },
  // "base" (Bichromatic) — a light theme (see isLightTheme).
  base: {
    font: '"IBM Plex Mono",monospace',
    bg: "#ffffff",
    panel: "#f5f5f5",
    past: "#fafafa",
    future: "#f5f5f5",
    purple: "#000000",
    purpleDim: "#777777",
    purpleL: "#000000",
    yellow: "#000000",
    gray: "#777777",
    grayL: "#333333",
    white: "#000000",
    orange: "#ff9933",
    green: "#44dd88",
    red: "#000000",
    danger: "#ff4455",
    border: "#cccccc",
    // Panel and rail edges: a step brighter than `border`, so panels stand off the canvas.
    edge: "#adadad",
    today: "#000000",
    pieBg: "#eeeeee",
    inputBg: "#f5f5f5",
    sidebarBg: "#fafafa",
    tooltipBg: "#eeeeee",
    rowInactive: "#ebebeb",
    rowActive: "#e6e6e6",
    rowSelectedEdge: "#000000",
    userMsg: "#f5f5f5",
    asstMsg: "#f5f5f5",
    asstBorder: "#cccccc",
    dropHover: "#e5e5e5",
    pastFill: "#eeeeee",
    pastHover: "#dddddd",
    btnGreen: "#eeeeee",
    btnRed: "#eeeeee",
    debugBg: "#fafafa",
    btnBg: "#eeeeee",
    sectionBg: "#f5f5f5",
    srcBg: "#f5f5f5",
    modalOverlay: "rgba(0,0,0,.4)",
    loadingOverlay: "rgba(255,255,255,0.9)",
    shadow: "rgba(0,0,0,.18)",
    shadowBox: "none",
    toolbarBg: "#fafafa",
    debugPanelBg: "#fafafa",
    toolbarBtn: "#eeeeee",
    disabledBorder: "#dddddd",
    redTeam: "#ff4455",
    blueTeam: "#4488ff",
    btnText: "#ffffff",
    chatInputBg: "#e5e5e5",
    action: "#000000",
    actGreen: "#44dd88",
    actRed: "#000000",
    actNeutral: "#333333",
    accent: "#000000",
    gold: "#b89b00",
  },
  // "dev" — mostly-monochrome base with a small, deliberate color vocabulary:
  //   • gold = clickable actions;
  //   • Sooth lavender = non-clickable accents and the tour tip color (the tour's "tip" green
  //     maps to the `green` token);
  //   • blueTeam / redTeam = trigger direction (raises / lowers).
  // `yellow` stays gray on purpose: it is mostly non-clickable section labels
  // (QUESTION/RESOLUTION/…). Dark-family theme (skips the light remap).
  dev: {
    font: '"IBM Plex Mono",monospace',
    bg: "#0e0d12",
    panel: "#15141b",
    past: "#0b0a0f",
    future: "#111016",
    purple: "#D783FF",
    purpleDim: "#6b6680",
    purpleL: "#D783FF",
    yellow: "#b9b5c6",
    gray: "#8b879b",
    grayL: "#c9c5d4",
    white: "#eceaf2",
    orange: "#e7b25c",
    green: "#D783FF",
    red: "#e8826e",
    danger: "#ff6b6b",
    border: "#2a2733",
    // Panel and rail edges: a step brighter than `border`, so panels stand off the canvas.
    edge: "#46414f",
    today: "#eceaf2",
    pieBg: "#15141b",
    inputBg: "#121117",
    sidebarBg: "#121117",
    tooltipBg: "#1c1a24",
    rowInactive: "#18171f",
    rowActive: "#201e29",
    rowSelectedEdge: "#D783FF",
    userMsg: "#1c1a24",
    asstMsg: "#141319",
    asstBorder: "#2a2733",
    dropHover: "#201e29",
    pastFill: "#15141b",
    pastHover: "#24222d",
    btnGreen: "#1c1a24",
    btnRed: "#1c1a24",
    debugBg: "#0e0d12",
    btnBg: "#1c1a24",
    sectionBg: "#15141b",
    srcBg: "#121117",
    modalOverlay: "rgba(5,4,8,.72)",
    loadingOverlay: "rgba(14,13,18,.86)",
    shadow: "rgba(0,0,0,.6)",
    shadowBox: "0 16px 48px rgba(0,0,0,.55)",
    toolbarBg: "#121117",
    debugPanelBg: "#0e0d12",
    toolbarBtn: "#1c1a24",
    disabledBorder: "#24222d",
    redTeam: "#e8826e",
    blueTeam: "#5fc4a0",
    btnText: "#0a0a0a",
    chatInputBg: "#18171f",
    action: "#D783FF",
    actGreen: "#D783FF",
    actRed: "#D783FF",
    actNeutral: "#D783FF",
    accent: "#D783FF",
    // Semantic gold for settled/winning/help places, distinct from `action`.
    gold: "#e7b25c",
  },
});
// Colorless-except-lavender pass: desaturates every palette color to its luminance grey
// except lavender/purple hues, so only the Sooth lavender accents show against an otherwise
// grey UI. It runs on the raw THEMES before they are frozen, so both the `--c-*` CSS-var
// emission and themeColors() inherit it. Delete this block to restore full color.
function _rgbToHS(r, g, b) {
  const mx = Math.max(r, g, b),
    mn = Math.min(r, g, b),
    d = mx - mn;
  let h = 0;
  if (d) {
    if (mx === r) h = ((g - b) / d) % 6;
    else if (mx === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h = h * 60;
    if (h < 0) h += 360;
  }
  return [h, mx === 0 ? 0 : d / mx];
}
function grayExceptLavender(str) {
  if (typeof str !== "string") return str;
  let r,
    g,
    b,
    a = null,
    hexAlpha = false;
  const hx = str.trim().match(/^#([0-9a-fA-F]{3,8})$/);
  if (hx) {
    let h = hx[1];
    if (h.length === 3 || h.length === 4)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    r = parseInt(h.slice(0, 2), 16);
    g = parseInt(h.slice(2, 4), 16);
    b = parseInt(h.slice(4, 6), 16);
    if (h.length === 8) {
      a = parseInt(h.slice(6, 8), 16) / 255;
      hexAlpha = true;
    }
  } else {
    const m = str
      .trim()
      .match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+)\s*)?\)$/);
    if (!m) return str; // composite value (font stack, box-shadow, gradient) → leave alone
    r = +m[1];
    g = +m[2];
    b = +m[3];
    a = m[4] !== undefined ? +m[4] : null;
  }
  const [h, s] = _rgbToHS(r, g, b);
  const two = (n) => n.toString(16).padStart(2, "0");
  if (s >= 0.15 && h >= 255 && h <= 300) return str;
  // Gold/yellow (the "action" accent) is deliberately recoloured to Sooth lavender, not greyed.
  if (s >= 0.4 && h >= 40 && h <= 65) {
    if (a !== null && !hexAlpha) return `rgba(215,131,255,${a})`;
    return a !== null ? `#D783FF${two(Math.round(a * 255))}` : "#D783FF";
  }
  const y = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
  if (a !== null && !hexAlpha) return `rgba(${y},${y},${y},${a})`;
  return a !== null
    ? `#${two(y)}${two(y)}${two(y)}${two(Math.round(a * 255))}`
    : `#${two(y)}${two(y)}${two(y)}`;
}
// Keys that must keep their literal colour: the pass would grey Dawn's green and turn
// Dark's gold lavender (`gold`), and a greyed `danger` reads as plain text.
const RAW_KEYS = new Set(["gold", "danger"]);
// The grey pass is off; the Dark and Dawn palettes are authored by role instead.
void RAW_KEYS;
void grayExceptLavender;

// `Object.freeze` above is shallow, so each palette is frozen separately.
for (const _name of Object.keys(THEMES)) Object.freeze(THEMES[_name]);

// The complete themed-key set (identical across every theme by construction; the
// colors.test.js key-parity check enforces it). Drives both the frozen `C` var map
// and GlobalStyles' `--c-*` emission, so a key can never be read as `C.x` without a
// matching `--c-x` being published.
export const THEME_KEYS = Object.freeze(Object.keys(THEMES.dark));

/** Does this theme draw dark ink on a light ground? Use it for lightness checks (surfaces,
 *  ink, grid, the light series remap); Dawn's own lavender choices stay keyed on `"dusk"`.
 *  @param {string} [theme]
 *  @returns {boolean} */
export function isLightTheme(theme) {
  return theme === "dusk" || theme === "base";
}

/** Raw-hex palette for JS-side computation that can't use a CSS variable: SVG `fill`/`stroke`
 *  attributes (which don't resolve `var()`), hex-alpha concatenation, and html2canvas/PDF
 *  export. `Object.hasOwn` makes inherited names like `"__proto__"` fall back to dark.
 *  @param {string} [theme]  A theme name; any value that isn't an own key of
 *    `THEMES` (including `undefined` or an unknown/inherited name) falls back to dark.
 *  @returns {Readonly<Record<string, string>>} */
export function themeColors(theme) {
  return Object.hasOwn(THEMES, theme) ? THEMES[theme] : THEMES.dark;
}

// Theme switches repaint consumers of `C` via the active `:root[data-theme=…]` block, with no
// JS re-render or module mutation.
export const C = Object.freeze(Object.fromEntries(THEME_KEYS.map((k) => [k, `var(--c-${k})`])));

// Deliberately a separate load-time "now" that mirrors App.jsx's module-level TODAY, so the
// series `past` flags agree with the app.
const TODAY = new Date();
const mkData = (rows) =>
  rows.map(([y, m, v]) => ({
    date: new Date(y, m, 1),
    value: v,
    past: new Date(y, m, 1) <= TODAY,
  }));
export const BASE_SERIES = {
  "S&P 500": {
    // Series colors must be literal hex: they are SVG stroke values and inputs to useTheme's
    // light-series remap, and a `var()` string would work for neither.
    color: "#44dd88",
    yMin: 2000,
    yMax: 9200,
    unit: "pts",
    data: mkData([
      [2019, 0, 2704],
      [2019, 3, 2946],
      [2019, 6, 2980],
      [2019, 9, 2977],
      [2019, 11, 3231],
      [2020, 0, 3226],
      [2020, 2, 2585],
      [2020, 3, 2447],
      [2020, 6, 3271],
      [2020, 11, 3756],
      [2021, 0, 3714],
      [2021, 3, 4181],
      [2021, 6, 4423],
      [2021, 11, 4766],
      [2022, 0, 4516],
      [2022, 5, 3785],
      [2022, 8, 3585],
      [2022, 11, 3840],
      [2023, 0, 4077],
      [2023, 5, 4450],
      [2023, 8, 4299],
      [2023, 11, 4769],
      [2024, 0, 4846],
      [2024, 3, 5035],
      [2024, 6, 5522],
      [2024, 11, 5881],
      [2025, 0, 5954],
      [2025, 3, 5692],
      [2025, 6, 5854],
      [2025, 11, 6198],
      [2026, 0, 6221],
      [2026, 2, 6150],
      [2026, 5, 6280],
      [2026, 11, 6520],
      [2027, 11, 6850],
      [2028, 11, 7200],
      [2029, 11, 7450],
      [2030, 11, 7800],
      [2034, 11, 9000],
    ]),
  },
  "WTI Crude": {
    color: "#ff9933",
    yMin: 10,
    yMax: 140,
    unit: "$/bbl",
    data: mkData([
      [2019, 0, 52],
      [2019, 5, 59],
      [2019, 11, 61],
      [2020, 0, 52],
      [2020, 2, 30],
      [2020, 3, 22],
      [2020, 8, 40],
      [2020, 11, 47],
      [2021, 0, 53],
      [2021, 5, 73],
      [2021, 9, 82],
      [2021, 11, 77],
      [2022, 0, 88],
      [2022, 5, 114],
      [2022, 8, 83],
      [2022, 11, 78],
      [2023, 0, 79],
      [2023, 8, 90],
      [2023, 11, 72],
      [2024, 0, 74],
      [2024, 3, 86],
      [2024, 8, 70],
      [2024, 11, 70],
      [2025, 0, 72],
      [2025, 5, 76],
      [2025, 11, 68],
      [2026, 0, 66],
      [2026, 11, 72],
      [2027, 11, 76],
      [2028, 11, 80],
      [2034, 11, 87],
    ]),
  },
};
export const KNOWN_SERIES = {
  gold: {
    color: "#F7CE46",
    yMin: 1100,
    yMax: 4800,
    unit: "$/oz",
    data: mkData([
      [2019, 0, 1282],
      [2019, 6, 1413],
      [2019, 11, 1478],
      [2020, 0, 1561],
      [2020, 3, 1686],
      [2020, 7, 2028],
      [2020, 11, 1898],
      [2021, 0, 1847],
      [2021, 5, 1770],
      [2021, 11, 1798],
      [2022, 0, 1798],
      [2022, 2, 1924],
      [2022, 8, 1711],
      [2022, 11, 1824],
      [2023, 0, 1924],
      [2023, 3, 2003],
      [2023, 11, 2063],
      [2024, 0, 2040],
      [2024, 2, 2230],
      [2024, 7, 2448],
      [2024, 11, 2625],
      [2025, 0, 2801],
      [2025, 2, 2980],
      [2025, 5, 3290],
      [2025, 11, 3050],
      [2026, 0, 3020],
      [2026, 2, 2950],
      [2026, 11, 3200],
      [2027, 11, 3350],
      [2028, 11, 3500],
      [2034, 11, 4800],
    ]),
  },
  nasdaq: {
    color: "#66ccff",
    yMin: 5000,
    yMax: 35000,
    unit: "pts",
    data: mkData([
      [2019, 0, 6635],
      [2019, 11, 8945],
      [2020, 0, 9150],
      [2020, 2, 7360],
      [2020, 6, 10208],
      [2020, 11, 12888],
      [2021, 5, 14504],
      [2021, 11, 15645],
      [2022, 0, 14239],
      [2022, 5, 11029],
      [2022, 11, 10939],
      [2023, 0, 11622],
      [2023, 5, 13787],
      [2023, 11, 16825],
      [2024, 5, 17862],
      [2024, 11, 19310],
      [2025, 0, 21184],
      [2025, 2, 18847],
      [2025, 11, 20500],
      [2026, 0, 20100],
      [2026, 2, 19800],
      [2026, 11, 22500],
      [2027, 11, 24000],
      [2028, 11, 25000],
      [2034, 11, 35000],
    ]),
  },
  bitcoin: {
    color: "#f7931a",
    yMin: 3000,
    yMax: 220000,
    unit: "USD",
    data: mkData([
      [2019, 0, 3500],
      [2019, 5, 8000],
      [2019, 11, 7200],
      [2020, 2, 5000],
      [2020, 6, 9100],
      [2020, 11, 28000],
      [2021, 3, 58000],
      [2021, 10, 61000],
      [2021, 11, 46000],
      [2022, 5, 29000],
      [2022, 11, 16500],
      [2023, 3, 28500],
      [2023, 11, 42000],
      [2024, 2, 63000],
      [2024, 11, 97000],
      [2025, 0, 102000],
      [2025, 2, 85000],
      [2025, 11, 125000],
      [2026, 0, 118000],
      [2026, 2, 112000],
      [2026, 11, 135000],
      [2027, 11, 145000],
      [2028, 11, 160000],
      [2034, 11, 220000],
    ]),
  },
  "10Y Treasury": {
    color: "#aaddff",
    yMin: 0.3,
    yMax: 6.5,
    unit: "% yield",
    data: mkData(
      [
        [2019, 0, 271],
        [2019, 3, 252],
        [2019, 6, 206],
        [2019, 11, 192],
        [2020, 0, 176],
        [2020, 2, 70],
        [2020, 6, 66],
        [2020, 11, 93],
        [2021, 0, 102],
        [2021, 2, 174],
        [2021, 6, 148],
        [2021, 11, 152],
        [2022, 0, 183],
        [2022, 2, 232],
        [2022, 5, 315],
        [2022, 8, 380],
        [2022, 11, 388],
        [2023, 0, 355],
        [2023, 5, 375],
        [2023, 7, 420],
        [2023, 9, 498],
        [2023, 11, 393],
        [2024, 0, 404],
        [2024, 3, 469],
        [2024, 6, 437],
        [2024, 11, 452],
        [2025, 0, 471],
        [2025, 2, 434],
        [2025, 5, 445],
        [2025, 11, 430],
        [2026, 0, 425],
        [2026, 11, 410],
        [2027, 11, 400],
        [2028, 11, 395],
        [2034, 11, 380],
      ].map(([y, m, v]) => [y, m, v / 100]),
    ),
  },
  "EUR/USD": {
    color: "#ffaa44",
    yMin: 0.95,
    yMax: 1.4,
    unit: "USD",
    data: mkData([
      [2019, 0, 1.14],
      [2019, 5, 1.12],
      [2019, 11, 1.12],
      [2020, 0, 1.11],
      [2020, 2, 1.08],
      [2020, 6, 1.18],
      [2020, 11, 1.22],
      [2021, 0, 1.22],
      [2021, 5, 1.19],
      [2021, 11, 1.13],
      [2022, 0, 1.13],
      [2022, 2, 1.1],
      [2022, 5, 1.05],
      [2022, 7, 0.99],
      [2022, 11, 1.07],
      [2023, 0, 1.09],
      [2023, 5, 1.09],
      [2023, 11, 1.1],
      [2024, 0, 1.08],
      [2024, 3, 1.07],
      [2024, 6, 1.09],
      [2024, 11, 1.05],
      [2025, 0, 1.04],
      [2025, 2, 1.08],
      [2025, 5, 1.13],
      [2025, 11, 1.09],
      [2026, 0, 1.07],
      [2026, 11, 1.1],
      [2027, 11, 1.12],
      [2028, 11, 1.13],
      [2034, 11, 1.15],
    ]),
  },
  "Natural Gas": {
    color: "#88ff88",
    yMin: 1.5,
    yMax: 10.0,
    unit: "$/MMBtu",
    data: mkData([
      [2019, 0, 3.1],
      [2019, 5, 2.4],
      [2019, 11, 2.2],
      [2020, 0, 2.1],
      [2020, 5, 1.7],
      [2020, 11, 2.6],
      [2021, 0, 2.7],
      [2021, 5, 3.2],
      [2021, 8, 5.1],
      [2021, 11, 3.8],
      [2022, 0, 4.0],
      [2022, 5, 7.2],
      [2022, 7, 9.0],
      [2022, 9, 5.4],
      [2022, 11, 5.6],
      [2023, 0, 3.5],
      [2023, 2, 2.1],
      [2023, 5, 2.2],
      [2023, 11, 2.5],
      [2024, 0, 2.7],
      [2024, 5, 2.2],
      [2024, 11, 3.2],
      [2025, 0, 3.8],
      [2025, 2, 4.1],
      [2025, 5, 3.6],
      [2025, 11, 3.3],
      [2026, 0, 3.2],
      [2026, 11, 3.5],
      [2027, 11, 3.7],
      [2028, 11, 4.0],
      [2034, 11, 4.5],
    ]),
  },
  Copper: {
    color: "#cc8844",
    yMin: 2.0,
    yMax: 7.0,
    unit: "$/lb",
    data: mkData([
      [2019, 0, 2.63],
      [2019, 5, 2.72],
      [2019, 11, 2.8],
      [2020, 0, 2.7],
      [2020, 2, 2.25],
      [2020, 6, 2.96],
      [2020, 11, 3.52],
      [2021, 0, 3.61],
      [2021, 4, 4.76],
      [2021, 11, 4.42],
      [2022, 0, 4.41],
      [2022, 2, 4.73],
      [2022, 6, 3.47],
      [2022, 11, 3.8],
      [2023, 0, 4.08],
      [2023, 5, 3.77],
      [2023, 11, 3.9],
      [2024, 0, 3.95],
      [2024, 4, 5.1],
      [2024, 6, 4.4],
      [2024, 11, 4.05],
      [2025, 0, 4.28],
      [2025, 2, 4.65],
      [2025, 5, 4.8],
      [2025, 11, 4.6],
      [2026, 0, 4.55],
      [2026, 11, 4.9],
      [2027, 11, 5.2],
      [2028, 11, 5.5],
      [2034, 11, 6.5],
    ]),
  },
  "US Population": {
    color: "#99ccff",
    yMin: 320,
    yMax: 380,
    unit: "M people",
    data: mkData([
      [2019, 0, 328],
      [2019, 6, 329],
      [2019, 11, 330],
      [2020, 0, 331],
      [2020, 6, 331],
      [2020, 11, 332],
      [2021, 0, 332],
      [2021, 6, 333],
      [2021, 11, 333],
      [2022, 0, 334],
      [2022, 6, 334],
      [2022, 11, 335],
      [2023, 0, 335],
      [2023, 6, 336],
      [2023, 11, 336],
      [2024, 0, 337],
      [2024, 6, 337],
      [2024, 11, 338],
      [2025, 0, 338],
      [2025, 6, 339],
      [2025, 11, 340],
      [2026, 0, 340],
      [2026, 11, 341],
      [2027, 11, 342],
      [2028, 11, 343],
      [2034, 11, 355],
    ]),
  },
  "US Unemployment": {
    color: "#ff99cc",
    yMin: 2,
    yMax: 15,
    unit: "% rate",
    data: mkData([
      [2019, 0, 4.0],
      [2019, 6, 3.7],
      [2019, 11, 3.5],
      [2020, 0, 3.6],
      [2020, 3, 14.7],
      [2020, 6, 11.1],
      [2020, 11, 6.7],
      [2021, 0, 6.4],
      [2021, 3, 6.0],
      [2021, 6, 5.9],
      [2021, 11, 3.9],
      [2022, 0, 4.0],
      [2022, 6, 3.6],
      [2022, 11, 3.5],
      [2023, 0, 3.4],
      [2023, 6, 3.6],
      [2023, 11, 3.7],
      [2024, 0, 3.7],
      [2024, 6, 4.1],
      [2024, 11, 4.2],
      [2025, 0, 4.1],
      [2025, 6, 4.3],
      [2025, 11, 4.2],
      [2026, 0, 4.1],
      [2026, 11, 4.0],
      [2027, 11, 3.9],
      [2028, 11, 3.8],
      [2034, 11, 4.0],
    ]),
  },
  Wheat: {
    color: "#ddcc66",
    yMin: 300,
    yMax: 1400,
    unit: "¢/bu",
    data: mkData([
      [2019, 0, 518],
      [2019, 6, 490],
      [2019, 11, 535],
      [2020, 0, 558],
      [2020, 6, 498],
      [2020, 11, 594],
      [2021, 0, 641],
      [2021, 6, 703],
      [2021, 11, 776],
      [2022, 0, 799],
      [2022, 2, 1277],
      [2022, 6, 842],
      [2022, 11, 733],
      [2023, 0, 733],
      [2023, 6, 635],
      [2023, 11, 602],
      [2024, 0, 582],
      [2024, 6, 545],
      [2024, 11, 530],
      [2025, 0, 548],
      [2025, 6, 560],
      [2025, 11, 540],
      [2026, 0, 545],
      [2026, 11, 580],
      [2027, 11, 600],
      [2028, 11, 620],
      [2034, 11, 700],
    ]),
  },
  Corn: {
    color: "#eecc44",
    yMin: 300,
    yMax: 850,
    unit: "¢/bu",
    data: mkData([
      [2019, 0, 373],
      [2019, 6, 420],
      [2019, 11, 387],
      [2020, 0, 380],
      [2020, 6, 321],
      [2020, 11, 430],
      [2021, 0, 498],
      [2021, 6, 672],
      [2021, 11, 591],
      [2022, 0, 639],
      [2022, 2, 803],
      [2022, 6, 587],
      [2022, 11, 663],
      [2023, 0, 660],
      [2023, 6, 498],
      [2023, 11, 472],
      [2024, 0, 437],
      [2024, 6, 395],
      [2024, 11, 432],
      [2025, 0, 452],
      [2025, 6, 445],
      [2025, 11, 440],
      [2026, 0, 448],
      [2026, 11, 470],
      [2027, 11, 490],
      [2028, 11, 510],
      [2034, 11, 580],
    ]),
  },
};

// Fallback colors for auto-assigned series when the caller supplies no color.
export const SERIES_PALETTE = [
  "#33ddcc",
  "#ff9933",
  "#cc99ff",
  "#ff4455",
  "#66ccff",
  "#F7CE46",
  "#f7931a",
];

// Raw hex (not the var-based `C`): the result is stored as the series' `.color` and rendered
// as an SVG stroke, where a var() string would not resolve. It must match the palette the
// two-axis chart uses.
export function pickSeriesSlotColor(theme, slot) {
  const tc = themeColors(theme);
  // `tc.white` is theme-aware ink (white in dev, dark in dusk), so the second slot stays
  // visible on each theme's background.
  if (theme === "dev" || theme === "dusk") return [tc.accent, tc.white][slot] || tc.action;
  return ["#33ddcc", tc.orange][slot] || "#33ddcc";
}

// Lifts the dotted timeline spline's faint gray dashes against the near-black background
// without lightening the shared gray/purple tokens used elsewhere.
const ONE_SHADE = 0x11;
export function lightenHex(hex, steps = 1) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const s = Number.isFinite(steps) ? Math.max(0, Math.floor(steps)) : 0;
  const add = ONE_SHADE * s;
  const lift = (v) => Math.min(255, v + add);
  const r = lift((n >> 16) & 0xff);
  const g = lift((n >> 8) & 0xff);
  const b = lift(n & 0xff);
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

// Prefer this over `lightenHex` for tints: a flat lift saturates channels that are already
// high, so the Sooth lavender #D783FF (blue at 255) drifts toward pink. Scaling each channel's
// remaining headroom keeps the hue.
export function tintHex(hex, amount = 0) {
  const m = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!m) return hex;
  const t = Number.isFinite(amount) ? Math.min(1, Math.max(0, amount)) : 0;
  const n = parseInt(m[1], 16);
  const mix = (v) => Math.round(v + (255 - v) * t);
  const r = mix((n >> 16) & 0xff);
  const g = mix((n >> 8) & 0xff);
  const b = mix(n & 0xff);
  return "#" + [r, g, b].map((v) => v.toString(16).padStart(2, "0")).join("");
}

export function addSeriesData(name, color) {
  const norm = name.toLowerCase().trim();
  const hit = Object.entries(KNOWN_SERIES).find(([k]) => {
    const kl = k.toLowerCase();
    return norm.includes(kl) || kl.includes(norm);
  });
  if (hit) return { ...hit[1], color };
  // Fallback: stub entry for unknown tickers — live data from server will fill in
  return { color, yMin: 0, yMax: 100, unit: "", data: [] };
}
